import { isStringNullOrWhiteSpace } from "owa-localize";
import { setFolderTreeDataMutator } from "../mutators/setFolderTreeData";
import FolderTreeLoadStateEnum from "../store/schema/FolderTreeLoadStateEnum";
import { addFolderToDefaultFolderMappings } from "owa-session-store";
import { createOrGetFolder } from "../orchestrators/helpers/createOrGetFolder";
import { updateFolderTable } from "../orchestrators/helpers/updateFolderTable";
import getFolderTable from "../selectors/getFolderTable";
import { isPstFileAccountInfraEnabled } from "owa-account-source-list/lib/flights";
import { isCoprincipalMailbox } from "owa-account-source-list-store";
import type { MailFolderFromServiceFragment } from "owa-mail-folder-view-graphql";
import type { MailFolder } from "owa-graph-schema";
import type { MailboxInfo } from "owa-client-types";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";

/**
 * Orchestrator that updates the folder table and sets-up parent child folder relations
 * for gql folder hierarchy response.
 */
export default async function onFolderHierarchyResponse(displayName: string | undefined, folders: MailFolderFromServiceFragment[], rootFolder: MailFolder, folderTreeIdentifier: string, includesLastItemInRange: boolean, offset: number, mailboxInfo: MailboxInfo | null, isCustomSorted: boolean): Promise<void> {
    updateFolderTable(displayName, rootFolder, folders, createOrGetFolder, isCustomSorted, mailboxInfo ?? undefined);
    
    const rootMailFolder = getFolderTable().get(rootFolder.id);
    if (rootMailFolder) {
        setFolderTreeDataMutator(folderTreeIdentifier, rootMailFolder, // Update folder table has added root to the table
        FolderTreeLoadStateEnum.Loaded, !includesLastItemInRange, // HasMoreData = !IncludesLastItemInRange
        offset, mailboxInfo, isCustomSorted);
    }
    
    // Ensure we have up-to-date folder id<->DistinguishedFolderId mappings for primary mailbox types
    // (UserMailbox, UnifiedInbox or PstFile) and archive mailboxes
    const mailboxType = rootFolder.mailboxInfo.type;
    const isUnifiedInbox = isUnifiedInboxMailboxInfo(rootFolder.mailboxInfo);
    const rootFolderId = rootFolder.id;
    const isPrimaryMailboxType = isPstFileAccountInfraEnabled()
        ? isCoprincipalMailbox(rootFolder.mailboxInfo)
        : mailboxType === "UserMailbox";
    if (isPrimaryMailboxType ||
        isUnifiedInbox ||
        mailboxType === "ArchiveMailbox" ||
        mailboxType === "SharedMailbox") {
        for (let i = 0; i < folders.length; i++) {
            const rawFolder = folders[i];
            const folderId = rawFolder.id;
            const distinguishedFolderType = rawFolder.distinguishedFolderType;
            const folderMailboxInfo = rawFolder.mailboxInfo;
            
            const shouldAdd = (isPrimaryMailboxType || rawFolder.parentFolderId == rootFolderId) &&
                !isStringNullOrWhiteSpace(distinguishedFolderType);
            
            if (shouldAdd && distinguishedFolderType) {
                addFolderToDefaultFolderMappings(distinguishedFolderType, folderId, folderMailboxInfo);
            }
        }
        
        if (rootFolder.distinguishedFolderType) {
            addFolderToDefaultFolderMappings(rootFolder.distinguishedFolderType, rootFolderId, rootFolder.mailboxInfo);
        }
        else if (mailboxType === "SharedMailbox") {
            // Add the msgfolderroot for shared resources, since it's not a distinguished folder
            // It needs to be in the mapping to ensure an "All folders" scope exists for mail search
            addFolderToDefaultFolderMappings("msgfolderroot", rootFolderId, rootFolder.mailboxInfo);
        }
    }
}
